import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

/** Points are fractions of the card box: x across, y down, 0..1. */
export type Pt = [number, number];
export type TabShape = { points: Pt[]; roundness: number };

export const DEFAULT_TAB_SHAPE: TabShape = {
  points: [
    [0.05, 0],
    [0.97, 0.07],
    [1, 1],
    [0, 1],
  ],
  roundness: 0.3,
};

const GREEN = "#00B14F";
const CREAM = "#FFF6EA";

/** Same rounding the app uses (quadratic corners), so the preview matches. */
export const shapePath = (shape: TabShape, w: number, h: number, inset = 0): string => {
  const pts: Pt[] = shape.points.map(([x, y]) => [
    inset + x * (w - inset * 2),
    inset + y * (h - inset * 2),
  ]);
  const r = Math.min(w - inset * 2, h - inset * 2) * shape.roundness;
  const n = pts.length;
  let d = "";
  for (let i = 0; i < n; i++) {
    const prev = pts[(i - 1 + n) % n];
    const cur = pts[i];
    const next = pts[(i + 1) % n];
    const toward = (a: Pt, b: Pt): Pt => {
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const len = Math.hypot(dx, dy) || 1;
      const k = Math.min(r, len / 2) / len;
      return [a[0] + dx * k, a[1] + dy * k];
    };
    const s = toward(cur, prev);
    const e = toward(cur, next);
    d += `${i === 0 ? "M" : "L"}${s[0].toFixed(1)},${s[1].toFixed(1)} `;
    d += `Q${cur[0].toFixed(1)},${cur[1].toFixed(1)} ${e[0].toFixed(1)},${e[1].toFixed(1)} `;
  }
  return `${d}Z`;
};

/** True when two non-adjacent edges cross (a twisted shape). */
export const selfIntersects = (pts: Pt[]): boolean => {
  const n = pts.length;
  const ccw = (a: Pt, b: Pt, c: Pt) => (c[1] - a[1]) * (b[0] - a[0]) > (b[1] - a[1]) * (c[0] - a[0]);
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (Math.abs(i - j) <= 1 || (i === 0 && j === n - 1)) continue;
      const [a, b] = [pts[i], pts[(i + 1) % n]];
      const [c, d] = [pts[j], pts[(j + 1) % n]];
      if (ccw(a, c, d) !== ccw(b, c, d) && ccw(a, b, c) !== ccw(a, b, d)) return true;
    }
  }
  return false;
};

export const area = (pts: Pt[]) =>
  Math.abs(
    pts.reduce((s, [x1, y1], i) => {
      const [x2, y2] = pts[(i + 1) % pts.length];
      return s + x1 * y2 - x2 * y1;
    }, 0),
  ) / 2;

const clamp01 = (v: number) => Math.min(1, Math.max(0, Math.round(v * 1000) / 1000));

/* Editing canvas: the card drawn large with draggable corner handles. */
const W = 320;
const H = 288; // the app's card is 0.9 as tall as it is wide
const PAD = 24;
const CANVAS_W = W;
const CANVAS_H = H;

export const ShapeCanvas: React.FC<{
  shape: TabShape;
  selected: number | null;
  onSelect: (i: number | null) => void;
  onChange: (points: Pt[], commit: boolean) => void;
  /** Canvas size in drawing units; defaults to the tab card's proportions. */
  width?: number;
  height?: number;
}> = ({ shape, selected, onSelect, onChange, width = CANVAS_W, height = CANVAS_H }) => {
  const W = width;
  const H = height;
  const svgRef = React.useRef<SVGSVGElement | null>(null);
  const drag = React.useRef<number | null>(null);

  const toNorm = (e: React.PointerEvent): Pt => {
    const rect = svgRef.current!.getBoundingClientRect();
    const sx = ((e.clientX - rect.left) / rect.width) * (W + PAD * 2) - PAD;
    const sy = ((e.clientY - rect.top) / rect.height) * (H + PAD * 2) - PAD;
    return [clamp01(sx / W), clamp01(sy / H)];
  };

  // Click on an edge: insert a point there.
  const onEdgeClick = (i: number, e: React.MouseEvent) => {
    if (shape.points.length >= 12) return;
    const rect = svgRef.current!.getBoundingClientRect();
    const p: Pt = [
      clamp01((((e.clientX - rect.left) / rect.width) * (W + PAD * 2) - PAD) / W),
      clamp01((((e.clientY - rect.top) / rect.height) * (H + PAD * 2) - PAD) / H),
    ];
    const next = [...shape.points];
    next.splice(i + 1, 0, p);
    onChange(next, true);
    onSelect(i + 1);
  };

  return (
    <svg
      ref={svgRef}
      viewBox={`${-PAD} ${-PAD} ${W + PAD * 2} ${H + PAD * 2}`}
      style={{ width: "100%", maxWidth: 380, touchAction: "none", userSelect: "none", display: "block" }}
      onPointerMove={(e) => {
        if (drag.current === null) return;
        const next = [...shape.points];
        next[drag.current] = toNorm(e);
        onChange(next, false);
      }}
      onPointerUp={() => {
        if (drag.current !== null) onChange(shape.points, true);
        drag.current = null;
      }}
      onPointerLeave={() => {
        if (drag.current !== null) onChange(shape.points, true);
        drag.current = null;
      }}
      onClick={(e) => {
        if (e.target === svgRef.current) onSelect(null);
      }}
    >
      {/* card box guide */}
      <rect x={0} y={0} width={W} height={H} fill="#F4F7F5" stroke="#C9D3CE" strokeDasharray="5 5" />
      <path d={shapePath(shape, W, H)} fill={GREEN} fillOpacity={0.9} />
      {/* thin straight polygon so the editable edges are visible */}
      <polygon
        points={shape.points.map(([x, y]) => `${x * W},${y * H}`).join(" ")}
        fill="none"
        stroke="#0A7A3C"
        strokeWidth={1}
        strokeDasharray="3 4"
      />
      {/* wide invisible edges: click to add a point */}
      {shape.points.map(([x, y], i) => {
        const [x2, y2] = shape.points[(i + 1) % shape.points.length];
        return (
          <line
            key={`e${i}`}
            x1={x * W}
            y1={y * H}
            x2={x2 * W}
            y2={y2 * H}
            stroke="transparent"
            strokeWidth={14}
            style={{ cursor: "copy" }}
            onClick={(e) => onEdgeClick(i, e)}
          />
        );
      })}
      {shape.points.map(([x, y], i) => (
        <circle
          key={`p${i}`}
          cx={x * W}
          cy={y * H}
          r={selected === i ? 11 : 9}
          fill={selected === i ? "#FFD54A" : "#FFFFFF"}
          stroke="#0A7A3C"
          strokeWidth={3}
          style={{ cursor: "grab" }}
          onPointerDown={(e) => {
            e.stopPropagation();
            (e.target as Element).setPointerCapture?.(e.pointerId);
            drag.current = i;
            onSelect(i);
          }}
        />
      ))}
    </svg>
  );
};

/* Preview: the row of four cards as the app shows it. */
export const ShapePreview: React.FC<{
  shape: TabShape;
  cards: Array<{ title: string; image?: string | null }>;
  width?: number;
}> = ({ shape, cards, width = 360 }) => {
  const overlap = 10;
  const cw = (width - 20 + overlap * 3) / 4;
  const ch = cw * 0.9;
  const ring = 3;
  return (
    <Box sx={{ bgcolor: GREEN, borderRadius: 3, p: "10px", width, maxWidth: "100%", overflowX: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {cards.slice(0, 4).map((c, i) => {
          const selected = i === 0;
          return (
            <Box
              key={i}
              sx={{
                position: "relative",
                width: cw,
                height: ch,
                flexShrink: 0,
                ml: i === 0 ? 0 : `${-overlap}px`,
                zIndex: selected ? 10 : 4 - i,
              }}
            >
              <svg width={cw} height={ch} style={{ position: "absolute", inset: 0 }}>
                <defs>
                  <linearGradient id={`pg${i}`} x1="0.15" y1="0" x2="0.85" y2="1">
                    <stop offset="0" stopColor="#3DDA83" />
                    <stop offset="0.55" stopColor="#14A954" />
                    <stop offset="1" stopColor="#0A8F43" />
                  </linearGradient>
                </defs>
                <path d={shapePath(shape, cw, ch, ring)} fill="rgba(0,45,20,0.18)" transform="translate(1,2)" />
                {selected ? (
                  <>
                    <path d={shapePath(shape, cw, ch, 0)} fill="rgba(255,255,255,0.42)" />
                    <path d={shapePath(shape, cw, ch, ring)} fill={CREAM} />
                  </>
                ) : (
                  <path d={shapePath(shape, cw, ch, ring)} fill={`url(#pg${i})`} stroke="rgba(255,255,255,0.32)" />
                )}
              </svg>
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  pl: i === 0 ? "4px" : `${overlap}px`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {c.image ? (
                  <img src={c.image} alt="" style={{ width: "62%", height: ch * 0.42, objectFit: "contain" }} />
                ) : (
                  <Box sx={{ height: ch * 0.42 }} />
                )}
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: selected ? 12 : 9,
                    lineHeight: 1.15,
                    textAlign: "center",
                    color: selected ? "#0A7A3C" : "#fff",
                    px: "2px",
                  }}
                >
                  {c.title}
                </Typography>
                <Box sx={{ width: 20, height: 3, borderRadius: 2, mt: "2px", bgcolor: selected ? "#0A7A3C" : "#FFD54A" }} />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/* The popup. */
export const TabShapeEditor: React.FC<{
  open: boolean;
  initial: TabShape;
  cards: Array<{ title: string; image?: string | null }>;
  saving: boolean;
  onClose: () => void;
  onApply: (shape: TabShape) => void;
}> = ({ open, initial, cards, saving, onClose, onApply }) => {
  const [shape, setShape] = React.useState<TabShape>(initial);
  const [history, setHistory] = React.useState<TabShape[]>([]);
  const [selected, setSelected] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (open) {
      setShape(initial);
      setHistory([]);
      setSelected(null);
    }
  }, [open, initial]);

  const commit = (next: TabShape) => {
    setHistory((h) => [...h.slice(-30), shape]);
    setShape(next);
  };

  // Drag frames update live; the drag's end is one undo step.
  const dragStart = React.useRef<TabShape | null>(null);
  const onPointsChange = (points: Pt[], done: boolean) => {
    if (!done) {
      if (!dragStart.current) dragStart.current = shape;
      setShape((s) => ({ ...s, points }));
      return;
    }
    const before = dragStart.current ?? shape;
    dragStart.current = null;
    setHistory((h) => [...h.slice(-30), before]);
    setShape((s) => ({ ...s, points }));
  };

  const removeSelected = () => {
    if (selected === null || shape.points.length <= 3) return;
    commit({ ...shape, points: shape.points.filter((_, i) => i !== selected) });
    setSelected(null);
  };

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === "Delete" || e.key === "Backspace") && selected !== null) {
        e.preventDefault();
        removeSelected();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const twisted = selfIntersects(shape.points);
  const tooSmall = area(shape.points) < 0.15;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Tab card shape</DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ShapeCanvas shape={shape} selected={selected} onSelect={setSelected} onChange={onPointsChange} />
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1 }}>
              Drag a point to move it · click an edge to add a point · select a point and press Delete
              to remove it ({shape.points.length}/12 points).
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, maxWidth: 380 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap" }}>Roundness</Typography>
              <Slider
                size="small"
                min={0}
                max={0.5}
                step={0.01}
                value={shape.roundness}
                onChange={(_, v) => setShape((s) => ({ ...s, roundness: v as number }))}
                onChangeCommitted={() => setHistory((h) => [...h.slice(-30), shape])}
                sx={{ color: GREEN }}
              />
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Tooltip title="Undo">
                <span>
                  <IconButton
                    disabled={!history.length}
                    onClick={() => {
                      setShape(history[history.length - 1]);
                      setHistory((h) => h.slice(0, -1));
                    }}
                  >
                    <UndoIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove selected point">
                <span>
                  <IconButton disabled={selected === null || shape.points.length <= 3} onClick={removeSelected}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                startIcon={<RestartAltIcon />}
                sx={{ textTransform: "none", color: "#0A7A3C" }}
                onClick={() => {
                  commit(DEFAULT_TAB_SHAPE);
                  setSelected(null);
                }}
              >
                Reset to default
              </Button>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Preview</Typography>
            <ShapePreview shape={shape} cards={cards} />
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1 }}>
              All four cards use this shape. The first is shown selected.
            </Typography>
            {twisted && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Two edges cross, so the card will look twisted. Move a point to untangle it.
              </Alert>
            )}
            {tooSmall && (
              <Alert severity="error" sx={{ mt: 2 }}>
                The shape is too small to hold the picture and name. Make it bigger to apply.
              </Alert>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving || tooSmall}
          onClick={() => onApply(shape)}
          sx={{ bgcolor: GREEN, "&:hover": { bgcolor: "#0a7a3c" }, textTransform: "none" }}
        >
          {saving ? "Applying…" : "Apply to app"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TabShapeEditor;
